import { useState, useEffect } from 'react';
import { useAuth, API } from '../../App';
import axios from 'axios';
import { toast } from 'sonner';
import { Shield, Check, X } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';

const SECTION_LABELS = {
  dashboard: 'Dashboard',
  rides: 'Rides',
  past_rides: 'Past Rides',
  users: 'Users',
  partners: 'Partners',
  vehicles: 'Vehicles',
  expenses: 'Expenses',
  petrol: 'Petrol',
  car_wash: 'Car Wash',
  financials: 'Financials',
  debts: 'Company Debts',
  daily_vouchers: 'Daily Vouchers',
  statistics: 'Statistics',
  partner_reports: 'Partner Reports',
  backup: 'Data Backup',
  integrations: 'Integrations',
  settings: 'Settings'
};

const SUB_PERMISSION_LABELS = {
  rides_edit: { label: 'Edit Rides (incl. change driver)', parent: 'rides' },
  rides_actions_others: { label: 'Press ride actions for other drivers', parent: 'rides' },
  past_rides_edit: { label: 'Edit Past Rides', parent: 'past_rides' },
  statistics_revenue: { label: 'View Revenue / Avg Ride', parent: 'statistics' },
  reports_download: { label: 'Download CSV Reports', parent: 'partner_reports' },
};

export default function PermissionsModal({ userId, userName, open, onClose }) {
  const { token } = useAuth();
  const [permissions, setPermissions] = useState({});
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open && userId) {
      fetchPermissions();
    }
  }, [open, userId]);

  const fetchPermissions = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/users/${userId}/permissions`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPermissions(response.data.permissions);
      setIsSuperAdmin(response.data.is_super_admin);
    } catch (error) {
      toast.error('Failed to load permissions');
    } finally {
      setLoading(false);
    }
  };

  const handleToggle = (section) => {
    setPermissions(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  const handleSelectAll = () => {
    const all = {};
    Object.keys(SECTION_LABELS).forEach(k => { all[k] = true; });
    Object.keys(SUB_PERMISSION_LABELS).forEach(k => { all[k] = true; });
    setPermissions(all);
  };

  const handleDeselectAll = () => {
    const none = {};
    Object.keys(SECTION_LABELS).forEach(k => { none[k] = false; });
    Object.keys(SUB_PERMISSION_LABELS).forEach(k => { none[k] = false; });
    setPermissions(none);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/users/${userId}/permissions`, {
        permissions
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`Permissions updated for ${userName}`);
      onClose();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update permissions');
    } finally {
      setSaving(false);
    }
  };

  const totalCount = Object.keys(SECTION_LABELS).length + Object.keys(SUB_PERMISSION_LABELS).length;
  const enabledCount = Object.keys({...SECTION_LABELS, ...SUB_PERMISSION_LABELS}).filter(k => permissions[k] !== false).length;

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Shield className="h-5 w-5 text-indigo-600" />
            Permissions: {userName}
          </DialogTitle>
        </DialogHeader>

        {loading ? (
          <div className="text-center py-8 text-slate-400">Loading...</div>
        ) : isSuperAdmin ? (
          <div className="text-center py-8">
            <Shield className="h-12 w-12 text-amber-500 mx-auto mb-3" />
            <p className="text-slate-600 font-medium">Super Admin</p>
            <p className="text-sm text-slate-400 mt-1">Has access to all sections. Cannot be restricted.</p>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="flex items-center justify-between text-sm text-slate-500 pb-2 border-b">
              <span>{enabledCount}/{totalCount} sections enabled</span>
              <div className="flex gap-2">
                <button onClick={handleSelectAll} className="text-indigo-600 hover:underline text-xs" data-testid="select-all-permissions">
                  Select All
                </button>
                <span className="text-slate-300">|</span>
                <button onClick={handleDeselectAll} className="text-red-600 hover:underline text-xs" data-testid="deselect-all-permissions">
                  Deselect All
                </button>
              </div>
            </div>

            <div className="space-y-1">
              {Object.entries(SECTION_LABELS).map(([key, label]) => (
                <div key={key}>
                  <div
                    className="flex items-center justify-between py-2.5 px-3 rounded-lg hover:bg-slate-50 transition-colors"
                    data-testid={`permission-toggle-${key}`}
                  >
                    <span className="text-sm font-medium text-slate-700">{label}</span>
                    <Switch
                      checked={permissions[key] !== false}
                      onCheckedChange={() => handleToggle(key)}
                    />
                  </div>
                  {/* Sub-permissions for this section */}
                  {Object.entries(SUB_PERMISSION_LABELS)
                    .filter(([, meta]) => meta.parent === key)
                    .map(([subKey, meta]) => (
                      <div
                        key={subKey}
                        className={`flex items-center justify-between py-2 px-3 pl-8 rounded-lg hover:bg-slate-50 transition-colors border-l-2 ml-3 ${permissions[key] === false ? 'opacity-40 pointer-events-none' : 'border-indigo-200'}`}
                        data-testid={`permission-toggle-${subKey}`}
                      >
                        <span className="text-xs font-medium text-slate-500">{meta.label}</span>
                        <Switch
                          checked={permissions[subKey] !== false}
                          onCheckedChange={() => handleToggle(subKey)}
                          disabled={permissions[key] === false}
                        />
                      </div>
                    ))
                  }
                </div>
              ))}
            </div>

            <div className="flex gap-3 pt-3 border-t">
              <Button
                variant="outline"
                onClick={onClose}
                className="flex-1"
              >
                Cancel
              </Button>
              <Button
                onClick={handleSave}
                disabled={saving}
                className="flex-1 bg-indigo-600 hover:bg-indigo-700 text-white"
                data-testid="save-permissions-btn"
              >
                {saving ? 'Saving...' : 'Save Permissions'}
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
